import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    // Restore user from localStorage for instant UI
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Persist user data for offline/PWA support
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      console.error('Auth error:', error);
      // Only logout if token is explicitly invalid (401)
      // Don't logout on network errors to preserve PWA session
      if (error.response?.status === 401) {
        // Token expired or invalid - clear session
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
      }
      // For network errors, keep the cached user data
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  // Periodic token validation (every 5 minutes when app is active)
  useEffect(() => {
    if (!token) return;
    
    const validateToken = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
        // Note: if access_restricted=true, ProtectedRoute will show the blocking
        // screen automatically on the next render. No need to force logout.
      } catch (error) {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete axios.defaults.headers.common['Authorization'];
          setToken(null);
          setUser(null);
        }
      }
    };

    // Poll every 30s for housekeepers (catch admin restriction), 5min otherwise
    const intervalMs = user?.role === 'housekeeper' ? 30000 : 300000;
    const interval = setInterval(validateToken, intervalMs);

    // Validate on visibility change (when user returns to app)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        validateToken();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token, user?.role]);

  const login = async (email, password, gps = null) => {
    const payload = { email, password };
    if (gps && Number.isFinite(gps.lat) && Number.isFinite(gps.lng)) {
      payload.gps_lat = gps.lat;
      payload.gps_lng = gps.lng;
    }
    const response = await axios.post(`${API}/auth/login`, payload);
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAdmin: user?.role === 'admin',
    isTech: user?.role === 'technicien',
    isHousekeeper: user?.role === 'housekeeper',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
